/* eslint-disable react/prop-types */
import { useState, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box,
  LinearProgress,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Grid from "@mui/material/Grid2";
import axios from "@/api/axios";
import { useSession } from "@/hooks/useSession";
import { useUsersSettingService } from "@/services/useUsersSettingService";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import PeopleIcon from "@mui/icons-material/People";
import SecurityIcon from "@mui/icons-material/Security";
import CommentIcon from "@mui/icons-material/Comment";
import DownloadIcon from "@mui/icons-material/Download";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import useGetMetadata from "@/hooks/useGetMetaData";
import ChecklistFormDialog from "./CheckListFormDialog";
import Comment from "./stepInput";
import InsertComment from "./insertComment";
import { convertUTCToLocal } from "@/lib/utils/dateUtils";
import { useTranslation } from "react-i18next";

// Import sub-components
import FileViewerDialog from "./components/FileViewerDialog";
import PermitInfoCard from "./components/PermitInfoCard";
import TableCard from "./components/TableCard";
import HazardsWithRiskLevels from "./components/HazardsWithRiskLevels";
import ModernCard from "./components/ModernCard";
import PrecautionsByCategory from "./components/PrecautionsByCategory";
import FilesTable from "./components/FilesTable";
import AIAnswerCard from "./components/AIAnswerCard";
import CommentsTable from "./components/CommentsTable";
import TimelineSection from "./components/TimelineSection";
import ChecklistsCard from "./components/ChecklistsCard";
import CertificatesDialog from "./components/CertificatesDialog";

// Main DocumentDetail Component
const DocumentDetail = ({ workPermit, onClose, onActionSuccess }) => {
  const { checklists, UsersList } = useGetMetadata();
  const { token, session } = useSession();
  const { userDetails, isUserDetailsLoading } = useUsersSettingService(
    session?.id
  );

  const { t } = useTranslation();
  const theme = useTheme();

  const [isCommentDialogOpen, setIsCommentDialogOpen] = useState(false);
  const [currentStepId, setCurrentStepId] = useState(null);
  const [currentStepName, setCurrentStepName] = useState("");
  const [isChecklistDialogOpen, setIsChecklistDialogOpen] = useState(false);
  const [selectedChecklist, setSelectedChecklist] = useState(null);
  const [commentedDocument, setCommentedDocument] = useState(null);
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const [stepInputs, setStepInputs] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileType, setSelectedFileType] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [selectedUserCertificates, setSelectedUserCertificates] = useState([]);
  const [certificatesDialogOpen, setCertificatesDialogOpen] = useState(false);
  const [selectedUserName, setSelectedUserName] = useState("");
  const [localComments, setLocalComments] = useState(
    workPermit?.comments || []
  );
  const [augmentedUsersList, setAugmentedUsersList] = useState(UsersList || []);

  const checklistObjects =
    workPermit?.checklists?.map(
      (checklist) => checklists?.find((c) => c.id === checklist.id) || checklist
    ) || [];

  // Sync localComments when workPermit changes
  useEffect(() => {
    if (workPermit?.comments) {
      setLocalComments(workPermit.comments);
    }
  }, [workPermit]);

  // Fetch missing users from step inputs and augment UsersList
  useEffect(() => {
    const fetchMissingUsers = async () => {
      if (
        !workPermit?.history_steps ||
        !Array.isArray(workPermit.history_steps)
      ) {
        return;
      }

      // Extract all user IDs from step_input_values
      const userIdsInSteps = new Set();
      workPermit.history_steps.forEach((step) => {
        if (step.step_input_values && Array.isArray(step.step_input_values)) {
          step.step_input_values.forEach((input) => {
            if (input.input_type === "USER_SELECTION" && input.raw_value) {
              userIdsInSteps.add(input.raw_value);
            } else if (
              input.input_type === "MULTI_USER_SELECTION" &&
              Array.isArray(input.raw_value)
            ) {
              input.raw_value.forEach((userId) => userIdsInSteps.add(userId));
            }
          });
        }
      });

      // Find missing users (not in UsersList)
      const currentUsersList = UsersList || [];
      const missingUserIds = Array.from(userIdsInSteps).filter(
        (userId) => !currentUsersList.some((user) => user.value === userId)
      );

      if (missingUserIds.length === 0) {
        // No missing users, use the original UsersList
        setAugmentedUsersList(currentUsersList);
        return;
      }

      // Fetch missing users from API
      const fetchedUsers = [];
      for (const userId of missingUserIds) {
        try {
          const response = await axios.get(`/api/v1/users/${userId}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (
            response.data &&
            response.data.first_name &&
            response.data.last_name
          ) {
            fetchedUsers.push({
              value: response.data.id,
              label: `${response.data.first_name} ${response.data.last_name}`,
            });
          }
        } catch (error) {
          console.error(`Error fetching user ${userId}:`, error);
          // Continue with other users even if one fails
        }
      }

      // Merge fetched users with existing UsersList
      if (fetchedUsers.length > 0) {
        setAugmentedUsersList([...currentUsersList, ...fetchedUsers]);
      } else {
        setAugmentedUsersList(currentUsersList);
      }
    };

    fetchMissingUsers();
  }, [workPermit, UsersList, token]);

  const handleCommentAdded = (newComment) => {
    // Add the new comment to the local comments array
    setLocalComments((prevComments) => [...prevComments, newComment]);
  };

  const getUserTimeZone = () => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (error) {
      console.error("Error getting time zone:", error);
      return "UTC";
    }
  };

  const fetchDocumentPdf = async () => {
    setIsDownloading(true);
    try {
      const workPermitId = workPermit.id;
      const zone = getUserTimeZone();
      const url = `/api/v1/workpermits/${workPermitId}/pdf?zone=${encodeURIComponent(zone)}`;

      const currentLanguage = localStorage.getItem("ln");

      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLanguage || "en",
        },
        responseType: "blob",
      });

      if (response.headers["content-type"] !== "application/pdf") {
        const text = await response.data.text();
        console.error("Unexpected response content:", text);
        throw new Error(t("documentDetail.errorNotPdf"));
      }

      if (response.data.size === 0) {
        throw new Error(t("documentDetail.errorEmptyPdf"));
      }

      const blob = new Blob([response.data], { type: "application/pdf" });
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `work_permit_${workPermit.id}.pdf`);
      document.body.appendChild(link);

      setTimeout(() => {
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
    } catch (error) {
      console.error("Error fetching PDF:", error);
      alert(t("documentDetail.errorDownloadPdf", { message: error.message }));
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadPdf = () => {
    if (!isDownloading) {
      fetchDocumentPdf();
    }
  };

  const getDuplicatedData = async () => {
    try {
      // Fetch precaution tree to build category map
      const precautionTreeResponse = await axios.get(
        "/api/v1/metadata/precaution-tree",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const precautionTree = precautionTreeResponse.data || [];

      // Build maps for quick lookup
      const precautionToCategoryMap = {};
      const categoryMap = {};

      precautionTree.forEach((category) => {
        categoryMap[category.category_id] = {
          id: category.category_id,
          name: category.category_name,
          precautions: [],
        };

        if (category.children && Array.isArray(category.children)) {
          category.children.forEach((precaution) => {
            precautionToCategoryMap[precaution.id] = {
              categoryId: category.category_id,
              categoryName: category.category_name,
            };
            categoryMap[category.category_id].precautions.push(precaution);
          });
        }
      });

      // Transform hazards from duplicated document to new structure with precautions
      const transformedHazards = (workPermit.hazards || []).map((hazard) => {
        // Extract precaution IDs from the hazard's precautions array
        const precautionIds = (hazard.precautions || []).map((p) => p.id);

        // Build precaution_categories structure
        const precautionCategories = [];
        const categoryIdsAdded = new Set();

        (hazard.precautions || []).forEach((precaution) => {
          const categoryInfo = precautionToCategoryMap[precaution.id];
          if (categoryInfo && !categoryIdsAdded.has(categoryInfo.categoryId)) {
            categoryIdsAdded.add(categoryInfo.categoryId);
            const category = categoryMap[categoryInfo.categoryId];
            if (category) {
              precautionCategories.push({
                id: category.id,
                name: category.name,
                precautions: category.precautions.filter((p) =>
                  precautionIds.includes(p.id)
                ),
              });
            }
          }
        });

        return {
          id: hazard.id,
          name: hazard.name,
          likelihood: hazard.likelihood || 0,
          severity_people: hazard.severity_people || 0,
          severity_asset: hazard.severity_asset || 0,
          severity_environment: hazard.severity_environment || 0,
          severity_reputation: hazard.severity_reputation || 0,
          precaution_categories: precautionCategories,
          precaution_ids: precautionIds,
          residual_risk: {
            hazard_id: hazard.id,
            likelihood: hazard.likelihood || 0,
            severity_people: hazard.severity_people || 0,
            severity_asset: hazard.severity_asset || 0,
            severity_environment: hazard.severity_environment || 0,
            severity_reputation: hazard.severity_reputation || 0,
          },
        };
      });

      // Store duplicated hazards separately so we don't overwrite allHazardsData
      if (transformedHazards.length > 0) {
        localStorage.setItem(
          "duplicatedWorkflowHazards",
          JSON.stringify(transformedHazards)
        );
      }

      return {
        work_flow_id: {
          value: workPermit.workflow?.id || "",
          label: workPermit.workflow?.name || "",
        },
        site_id: {
          value: workPermit.site?.id || "",
          label: workPermit.site?.name || "",
        },
        checklist_ids:
          workPermit.checklists?.map((c) => ({
            value: c.id,
            label: c.name,
          })) || [],
        title: workPermit.title || "",
        description: workPermit.description || "",
        start_datetime: "",
        hazards: transformedHazards,
        performing_user_ids:
          workPermit.performing_users?.map((u) => ({
            value: u.id,
            label: `${u.first_name} ${u.last_name}`,
          })) || [],
        stepInputsData: {},
        allHazardsData: [],
      };
    } catch (error) {
      console.error("Error in getDuplicatedData:", error);
      // Fallback with empty precaution_categories if API fails
      const transformedHazards = (workPermit.hazards || []).map((hazard) => {
        const precautionIds = (hazard.precautions || []).map((p) => p.id);
        return {
          id: hazard.id,
          name: hazard.name,
          likelihood: hazard.likelihood || 0,
          severity_people: hazard.severity_people || 0,
          severity_asset: hazard.severity_asset || 0,
          severity_environment: hazard.severity_environment || 0,
          severity_reputation: hazard.severity_reputation || 0,
          precaution_categories: [],
          precaution_ids: precautionIds,
          residual_risk: {
            hazard_id: hazard.id,
            likelihood: hazard.likelihood || 0,
            severity_people: hazard.severity_people || 0,
            severity_asset: hazard.severity_asset || 0,
            severity_environment: hazard.severity_environment || 0,
            severity_reputation: hazard.severity_reputation || 0,
          },
        };
      });

      return {
        work_flow_id: {
          value: workPermit.workflow?.id || "",
          label: workPermit.workflow?.name || "",
        },
        site_id: {
          value: workPermit.site?.id || "",
          label: workPermit.site?.name || "",
        },
        checklist_ids:
          workPermit.checklists?.map((c) => ({
            value: c.id,
            label: c.name,
          })) || [],
        title: workPermit.title || "",
        description: workPermit.description || "",
        start_datetime: "",
        hazards: transformedHazards,
        performing_user_ids:
          workPermit.performing_users?.map((u) => ({
            value: u.id,
            label: `${u.first_name} ${u.last_name}`,
          })) || [],
        stepInputsData: {},
        allHazardsData: [],
      };
    }
  };

  const handleDuplicateDocument = async () => {
    try {
      const duplicatedData = await getDuplicatedData();

      // Automatically remove legacy precautions (precautions not in current metadata)
      const precautionIdSet = new Set();

      // Fetch current precaution tree to get valid precaution IDs
      try {
        const precautionTreeResponse = await axios.get(
          "/api/v1/metadata/precaution-tree",
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const precautionTree = precautionTreeResponse.data || [];

        // Build set of current precaution IDs
        precautionTree.forEach((category) => {
          if (category.children && Array.isArray(category.children)) {
            category.children.forEach((precaution) => {
              precautionIdSet.add(precaution.id);
            });
          }
        });

        // Filter out legacy precautions from duplicated data
        const cleanedData = {
          ...duplicatedData,
          hazards: duplicatedData.hazards.map((hazard) => ({
            ...hazard,
            precaution_ids: hazard.precaution_ids.filter((id) =>
              precautionIdSet.has(id)
            ),
            precaution_categories: hazard.precaution_categories.map(
              (category) => ({
                ...category,
                precautions: category.precautions.filter((p) =>
                  precautionIdSet.has(p.id)
                ),
              })
            ),
          })),
        };

        // Also filter out from stored duplicatedWorkflowHazards if it exists
        const storedHazards = localStorage.getItem("duplicatedWorkflowHazards");
        if (storedHazards) {
          try {
            const hazards = JSON.parse(storedHazards);
            const cleanedHazards = hazards.map((hazard) => ({
              ...hazard,
              precaution_ids: hazard.precaution_ids.filter((id) =>
                precautionIdSet.has(id)
              ),
              precaution_categories: hazard.precaution_categories.map(
                (category) => ({
                  ...category,
                  precautions: category.precautions.filter((p) =>
                    precautionIdSet.has(p.id)
                  ),
                })
              ),
            }));
            localStorage.setItem(
              "duplicatedWorkflowHazards",
              JSON.stringify(cleanedHazards)
            );
          } catch (error) {
            console.error("Error cleaning duplicated workflow hazards:", error);
          }
        }

        localStorage.setItem("duplicatedFormData", JSON.stringify(cleanedData));
        // Remove legacy precautions from localStorage since we're not keeping them
        localStorage.removeItem("legacyPrecautions");
        window.location.href = "/newptw";
      } catch (error) {
        console.error("Error fetching precaution tree:", error);
        // If there's an error fetching the tree, proceed with original data
        localStorage.setItem(
          "duplicatedFormData",
          JSON.stringify(duplicatedData)
        );
        window.location.href = "/newptw";
      }
    } catch (error) {
      console.error("Error duplicating document:", error);
      alert(t("documentDetail.errorDuplicatingDocument"));
    }
  };

  const handleForwardDocument = () => {
    // Create a shareable URL for the document using the route /doc/:documentId
    const documentUrl = `${window.location.origin}/doc/${workPermit.id}`;

    // Email subject
    const subject = encodeURIComponent(
      t("documentDetail.forwardEmailSubject", {
        title: workPermit.title || "Work Permit",
        workflow: workPermit.workflow?.name || "Work Permit",
      })
    );

    // Email body - just the URL link
    const body = encodeURIComponent(documentUrl);

    // Open email client with pre-filled content
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const handleComment = useCallback((documentId) => {
    setCommentedDocument(documentId);
    setCommentModalOpen(true);
  }, []);

  const handleStep = useCallback((step) => {
    setCurrentStepId(step.step_id);
    setCurrentStepName(step.action);
    setStepInputs(Array.isArray(step.step_inputs) ? step.step_inputs : []);
    setIsCommentDialogOpen(true);
  }, []);

  const handleFileClick = useCallback((url) => {
    const cleanUrl = url.split("?")[0];
    const ext = cleanUrl.split(".").pop().toLowerCase();
    if (ext === "pdf") {
      setSelectedFileType("pdf");
    } else if (["doc", "docx", "xls", "xlsx", "ppt", "pptx"].includes(ext)) {
      setSelectedFileType("office");
    } else if (
      ["jpg", "jpeg", "png", "gif", "bmp", "webp", "tiff"].includes(ext)
    ) {
      setSelectedFileType("image");
    } else {
      window.open(url, "_blank");
      return;
    }
    setSelectedFile(url);
  }, []);

  const handleViewChecklist = (checklist) => {
    setSelectedChecklist(checklist);
    setIsChecklistDialogOpen(true);
  };

  const handleViewCertificates = async (userId, firstName, lastName) => {
    try {
      const response = await axios.get(`/api/v1/users/${userId}/certificates`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setSelectedUserCertificates(response.data);
      setSelectedUserName(`${firstName} ${lastName}`);
      setCertificatesDialogOpen(true);
    } catch (error) {
      console.error("Error fetching certificates:", error);
      alert(t("documentDetail.errorFetchingCertificates"));
    }
  };

  const computeCertificateStatus = (cert) => {
    if (!cert.expires) {
      return {
        label: t("documentDetail.active"),
        severity: "success",
      };
    }

    const currentDate = new Date();
    const expiresDate = new Date(cert.expires_at);

    if (expiresDate < currentDate) {
      return {
        label: t("documentDetail.expired"),
        severity: "error",
      };
    }

    const timeDiff = expiresDate.getTime() - currentDate.getTime();
    const daysLeft = Math.ceil(timeDiff / (1000 * 3600 * 24));

    if (daysLeft <= 90) {
      return {
        label: t("documentDetail.willExpireIn", { days: daysLeft }),
        severity: "warning",
      };
    }

    return {
      label: t("documentDetail.active"),
      severity: "success",
    };
  };

  if (!workPermit || isUserDetailsLoading) {
    return (
      <Dialog
        open
        maxWidth="xl"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            width: "95%",
            maxWidth: 1400,
            boxShadow: theme.shadows[20],
          },
        }}
      >
        <DialogTitle
          sx={{ bgcolor: "primary.main", color: "primary.contrastText" }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h6">
              {t("documentDetail.dialogTitle")}
            </Typography>
            <IconButton onClick={onClose} sx={{ color: "inherit" }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 400,
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography>{t("documentDetail.loading")}</Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog
        open
        onClose={onClose}
        maxWidth="xl"
        fullWidth
        aria-labelledby="ptw-details-dialog-title"
        sx={{
          "& .MuiDialog-paper": {
            width: "95%",
            maxWidth: 1400,
            boxShadow: theme.shadows[20],
            transition: "all 0.3s ease-in-out",
          },
        }}
      >
        <DialogTitle
          id="ptw-details-dialog-title"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: 2,
            px: 3,
            bgcolor: "primary.main",
            color: "primary.contrastText",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <DescriptionIcon sx={{ fontSize: 28 }} />
            <Typography component="span" variant="h5" fontWeight="bold">
              {t("documentDetail.dialogTitle")}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            aria-label={t("documentDetail.closeDialogAria")}
            sx={{ color: "inherit" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, bgcolor: "background.default" }}>
          {/* Progress indicator */}
          {isDownloading && (
            <LinearProgress sx={{ position: "sticky", top: 0, zIndex: 1000 }} />
          )}

          <Box sx={{ p: 4 }}>
            {/* Main Document Info Card */}
            <PermitInfoCard
              workPermit={workPermit}
              convertUTCToLocal={convertUTCToLocal}
              t={t}
            />

            {/* Performing Users */}
            <TableCard
              title={t("documentDetail.performingUsersTitle")}
              data={workPermit.performing_users || []}
              keys={["first_name", "last_name"]}
              subtitles={[
                t("documentDetail.firstName"),
                t("documentDetail.lastName"),
              ]}
              icon={PeopleIcon}
              t={t}
              color="info"
              renderActions={(user) => (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() =>
                    handleViewCertificates(
                      user.id,
                      user.first_name,
                      user.last_name
                    )
                  }
                >
                  {t("documentDetail.viewCertificates")}
                </Button>
              )}
            />

            {/* Hazards with Risk Levels and Precautions Grid */}
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <HazardsWithRiskLevels
                  hazards={workPermit.hazards || []}
                  documentCreatedAt={workPermit.created_at}
                  t={t}
                  theme={theme}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <ModernCard
                  title={t("documentDetail.precautionsTitle")}
                  icon={SecurityIcon}
                  color="warning"
                >
                  <PrecautionsByCategory
                    workPermit={workPermit}
                    t={t}
                    theme={theme}
                  />
                </ModernCard>
              </Grid>
            </Grid>

            {/* Files */}
            {workPermit.attachments.length > 0 && (
              <FilesTable
                attachments={workPermit.attachments || []}
                formatCreatedAt={convertUTCToLocal}
                onFileClick={handleFileClick}
                t={t}
              />
            )}

            {/* AI Answer */}
            <AIAnswerCard aiAnswer={workPermit.ai_answer} t={t} />

            {/* Comments */}
            <CommentsTable
              comments={localComments}
              formatCreatedAt={convertUTCToLocal}
              onFileClick={handleFileClick}
              t={t}
            />

            {/* Timeline */}
            <TimelineSection
              historySteps={workPermit.history_steps || []}
              convertUTCToLocal={convertUTCToLocal}
              onFileClick={handleFileClick}
              UsersList={augmentedUsersList}
              t={t}
            />

            {/* Checklists */}
            <ChecklistsCard
              checklistObjects={checklistObjects}
              onViewChecklist={handleViewChecklist}
              t={t}
            />

            {/* Add Comment Button */}
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<CommentIcon />}
                onClick={() => handleComment(workPermit.id)}
                sx={{
                  borderRadius: 3,
                  px: 4,
                  py: 1.5,
                  boxShadow: theme.shadows[4],
                  "&:hover": {
                    boxShadow: theme.shadows[8],
                  },
                }}
              >
                {t("documentDetail.addCommentButton")}
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: "background.paper" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "end",
              gap: 4,
              width: "100%",
            }}
          >
            <Grid container spacing={1} justifyContent="center" width="100%">
              {workPermit.next_steps?.map((step) => {
                // Ensure step.user_groups exists and is an array
                const stepUserGroups = step.user_groups || [];
                const userGroups = userDetails?.user_groups || [];

                // Check if user has matching user group
                const hasMatchingUserGroup = stepUserGroups.some((stepGroup) =>
                  userGroups.some((userGroup) => userGroup.id === stepGroup.id)
                );

                // For non-self-actionable steps, check if user is not the author
                // For self-actionable steps, no author check needed
                const isAuthorCheck =
                  step.self_actionable ||
                  workPermit.author?.id !== userDetails?.id;

                // Collect all reasons why the button might be disabled
                const disableReasons = [];
                if (!hasMatchingUserGroup) {
                  disableReasons.push(
                    t("documentDetail.noMatchingUserGroup", {
                      groups: step.user_groups.map((g) => g.name).join(", "),
                    })
                  );
                }
                if (!isAuthorCheck) {
                  disableReasons.push(t("documentDetail.authorCannotPerform"));
                }

                // Button should be disabled if there are any reasons
                const isDisabled = disableReasons.length > 0;

                return (
                  <Grid size={{ xs: 12, sm: 6, md: 2 }} key={step.step_id}>
                    <Tooltip
                      title={
                        isDisabled
                          ? t("documentDetail.buttonDisabledReason", {
                              reason: disableReasons.join("; "),
                            })
                          : ""
                      }
                      placement="top"
                    >
                      <span>
                        <Button
                          onClick={() => handleStep(step)}
                          variant="contained"
                          color="primary"
                          fullWidth
                          disabled={isDisabled}
                          sx={{
                            borderRadius: 2,
                            px: 4,
                            whiteSpace: "nowrap",
                            "&.Mui-disabled": {
                              bgcolor: theme.palette.action.disabledBackground,
                              color: theme.palette.action.disabled,
                            },
                          }}
                        >
                          {step.action}
                        </Button>
                      </span>
                    </Tooltip>
                  </Grid>
                );
              })}
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadPdf}
                  disabled={isDownloading}
                  fullWidth
                  sx={{
                    borderRadius: 2,
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                  }}
                >
                  {isDownloading
                    ? t("documentDetail.downloading")
                    : t("documentDetail.downloadButton")}
                </Button>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<FileCopyIcon />}
                  onClick={handleDuplicateDocument}
                  fullWidth
                  sx={{
                    borderRadius: 2,
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                  }}
                >
                  {t("documentDetail.duplicateButton")}
                </Button>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<ForwardToInboxIcon />}
                  onClick={handleForwardDocument}
                  fullWidth
                  sx={{
                    borderRadius: 2,
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                  }}
                >
                  {t("documentDetail.forwardButton")}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </DialogActions>
      </Dialog>

      {commentedDocument && (
        <InsertComment
          commentedDocument={commentedDocument}
          commentModalOpen={commentModalOpen}
          setCommentModalOpen={setCommentModalOpen}
          onCommentAdded={handleCommentAdded}
        />
      )}

      <Comment
        isCommentDialogOpen={isCommentDialogOpen}
        aicomment={workPermit.ai_answer}
        setIsCommentDialogOpen={setIsCommentDialogOpen}
        currentStepId={currentStepId}
        documentId={workPermit.id}
        stepName={currentStepName}
        stepInputs={stepInputs}
        onClose={onClose}
        onSuccess={onActionSuccess}
      />

      {selectedFile && (
        <FileViewerDialog
          fileUrl={selectedFile}
          fileType={selectedFileType}
          onClose={() => setSelectedFile(null)}
          t={t}
        />
      )}

      {selectedChecklist && (
        <ChecklistFormDialog
          open={isChecklistDialogOpen}
          onClose={() => {
            setIsChecklistDialogOpen(false);
            setSelectedChecklist(null);
          }}
          checklist={selectedChecklist}
          documentId={workPermit.id}
        />
      )}

      <CertificatesDialog
        open={certificatesDialogOpen}
        onClose={() => setCertificatesDialogOpen(false)}
        selectedUserName={selectedUserName}
        selectedUserCertificates={selectedUserCertificates}
        convertUTCToLocal={convertUTCToLocal}
        onFileClick={handleFileClick}
        computeCertificateStatus={computeCertificateStatus}
        t={t}
        theme={theme}
      />
    </>
  );
};

export default DocumentDetail;
